// The trade builder (TRADES.md "The builder"): partner, give and get side
// by side (segmented on phone), drops when a roster would be over, the
// three read-out lines, a note, Send. Opens in a DetailSheet; a "Trade for"
// link seeds it with the partner and the player.
//
// A CLEAN SCREEN (eslint.config.js, CLEAN_SCREENS). The same component for a
// practice league and a real one: `useTradeBuilder` asks the simulator or
// `GET …/trades/builder` as boxes are checked, and holds its last answer on
// screen while the next one is in flight, so a checkbox never blanks it.

import { useEffect, useRef, useState } from 'react';
import type { DraftPickRef, TradeBuilderView, TradeRosterRow } from '@fsp/domain';
import { failureOf, valueOf } from '@fsp/domain';
import type { BuilderInput } from '@fsp/trades';
import { useTradeBuilder } from '../../data/hooks';
import type { TradeActions } from '../../data/trades';
import { useDelight } from '../../delight/useDelight';
import { GameLabel, PlayerMarks, PositionPill, TeamMark } from '../../kit';
import {
  Button,
  Callout,
  Caption,
  Card,
  Line,
  ListRow,
  PinnedTop,
  RowName,
  RowSub,
  Segments,
  Stack,
  Text,
  TextArea,
  Tick,
  tell,
} from '../../kit/ui';
import { useTier } from '../../shell/useTier';
import { DetailSheet } from '../../templates/DetailSheet';
import { num } from './notComputed';
import { Proj, Worth } from './Projection';
import { useTools } from '../../data/tools';

export type BuilderSeed = Partial<BuilderInput> & { open: true };

function RosterPicker({
  rows,
  picked,
  onToggle,
  full: sideFull,
  emptyText,
}: {
  rows: TradeRosterRow[];
  picked: string[];
  onToggle: (id: string) => void;
  /** The side has as many players and picks as it may send. */
  full: boolean;
  emptyText: string;
}) {
  if (rows.length === 0) return <div className="fsp-empty">{emptyText}</div>;
  return (
    <Card>
      {rows.map((r) => {
        const on = picked.includes(r.player.id);
        const full = !on && sideFull;
        return (
          <ListRow
            key={r.player.id}
            lead={
              <Line gap={2}>
                <Tick on={on} />
                <PositionPill pos={r.player.positions[0] ?? r.slot} size="sm" />
              </Line>
            }
            leadSize="xl"
            checked={on}
            selected={on}
            dim={full}
            onClick={() => onToggle(r.player.id)}
            trail={
              <Text tone="secondary" size="xs" numeric>
                {/* Neither in a league that keeps neither (RULES_SCHEMA §15c). */}
                {r.worth !== null ? (
                  <Worth>{num(r.worth)}</Worth>
                ) : r.projection !== null ? (
                  <Proj>{num(r.projection)}</Proj>
                ) : null}
              </Text>
            }
          >
            <RowName marks={<PlayerMarks player={r.player} />}>{r.player.name}</RowName>
            <RowSub>
              <GameLabel game={r.game} />
              {r.locked ? ' · game started, goes through after the week' : null}
            </RowSub>
          </ListRow>
        );
      })}
    </Card>
  );
}

/**
 * A side's draft picks to choose (PLAN-2026-10-27-the-next-season §2.6),
 * under its players: each by season and round, its place once the order is
 * set, whose seat it is when it is not the holder's own, and what it counts.
 */
function PickPicker({
  picks,
  picked,
  onToggle,
  full: sideFull,
  mine,
}: {
  picks: DraftPickRef[];
  picked: string[];
  onToggle: (id: string) => void;
  full: boolean;
  /** The reader's own side: its own picks are "Your own pick". */
  mine: boolean;
}) {
  if (picks.length === 0) return null;
  return (
    <Stack gap={2}>
      <Caption>Draft picks</Caption>
      <Card>
        {picks.map((p) => {
          const on = picked.includes(p.id);
          const full = !on && sideFull;
          const own = p.holder !== null && p.holder.id === p.team.id;
          return (
            <ListRow
              key={p.id}
              lead={<Tick on={on} />}
              leadSize="sm"
              checked={on}
              selected={on}
              dim={full}
              onClick={() => onToggle(p.id)}
              trail={
                p.worth !== null ? (
                  <Text tone="secondary" size="xs" numeric>
                    <Worth>{num(p.worth)}</Worth>
                  </Text>
                ) : undefined
              }
            >
              <RowName>
                {p.season} · Round {p.round}
                {p.place !== null ? ` · ${p.round}.${String(p.place).padStart(2, '0')}` : ''}
              </RowName>
              <RowSub>
                {own ? (mine ? 'Your own pick' : 'Their own pick') : `${p.team.name}’s pick`}
              </RowSub>
            </ListRow>
          );
        })}
      </Card>
    </Stack>
  );
}

export function TradeBuilder({
  seed,
  actions,
  onClose,
}: {
  seed: BuilderSeed | null;
  actions: TradeActions;
  onClose: () => void;
}) {
  const tools = useTools();
  const { play } = useDelight();
  const phone = useTier() === 'phone';
  const [partnerTeamId, setPartner] = useState<string | null>(null);
  const [give, setGive] = useState<string[]>([]);
  const [get, setGet] = useState<string[]>([]);
  const [givePicks, setGivePicks] = useState<string[]>([]);
  const [getPicks, setGetPicks] = useState<string[]>([]);
  const [myDrops, setMyDrops] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [side, setSide] = useState<'give' | 'get'>('get');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const key = seed ? JSON.stringify(seed) : '';
  const held = useRef<TradeBuilderView | null>(null);
  useEffect(() => {
    setPartner(seed?.partnerTeamId ?? null);
    setGive(seed?.give ?? []);
    setGet(seed?.get ?? []);
    setGivePicks(seed?.givePicks ?? []);
    setGetPicks(seed?.getPicks ?? []);
    setMyDrops(seed?.myDrops ?? []);
    setNote('');
    setSide('get');
    setError(null);
    held.current = null;
  }, [key]);
  const snapshot = useTradeBuilder(
    seed
      ? {
          partnerTeamId,
          give,
          get,
          givePicks,
          getPicks,
          myDrops,
          counterOf: seed.counterOf ?? null,
        }
      : null,
  );
  const fresh = snapshot ? valueOf(snapshot) : null;
  if (fresh) held.current = fresh;
  const view = fresh ?? held.current;
  const failure = snapshot ? failureOf(snapshot) : null;
  if (!seed) return <DetailSheet opened={false} onClose={onClose} children={null} />;
  if (!view)
    return (
      <DetailSheet opened onClose={onClose} title="New trade" wide>
        {failure ? (
          <Callout tone="bad">{failure.message}</Callout>
        ) : (
          <Text tone="secondary">Loading the rosters…</Text>
        )}
      </DetailSheet>
    );

  const toggle = (list: string[], set: (v: string[]) => void, id: string) =>
    set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const sendIt = () => {
    // ON THE ANSWER TO WHAT IS PICKED NOW, never the one before it, which
    // stays on screen while the next loads (the review of chunk 1).
    if (!view.partner || busy || fresh === null) return;
    const partner = view.partner;
    setError(null);
    setBusy(true);
    void actions
      .propose({
        to: partner.id,
        give,
        get,
        // ONLY THE PICKS THE VIEW STILL LISTS: one traded since is gone from it.
        givePicks: givePicks.filter((id) => fresh.myPicks.some((p) => p.id === id)),
        getPicks: getPicks.filter((id) => fresh.theirPicks.some((p) => p.id === id)),
        // THE DROPS THE VIEW SHOWS, and only while it shows them: a drop picked
        // and then traded, or no longer needed, was sent anyway (the trade
        // review of 2026-10-22, production's two 400s).
        myDrops: fresh.mineNeedsDrop ? myDrops.filter((id) => !give.includes(id)) : [],
        note,
        counterOf: seed.counterOf ?? null,
      })
      .then((outcome) => {
        setBusy(false);
        if (!outcome.ok) {
          setError(outcome.message);
          return;
        }
        play('lock');
        tell(
          seed.counterOf
            ? `Counter sent to ${partner.ownerName}.`
            : `Sent to ${partner.ownerName}. They have 3 days.`,
          'good',
        );
        onClose();
      });
  };

  // PLAYERS AND PICKS TOGETHER count toward the per-side limit (§2.6 rule 3).
  const giveCount = give.length + givePicks.length;
  const getCount = get.length + getPicks.length;
  const anyPicks = view.myPicks.length + view.theirPicks.length > 0;
  const giveList = (
    <Stack gap={3}>
      <RosterPicker
        rows={view.mine}
        picked={give}
        onToggle={(id) => {
          toggle(give, setGive, id);
          // A player traded is no longer one to drop.
          setMyDrops((d) => d.filter((x) => x !== id));
        }}
        full={giveCount >= view.maxPerSide}
        emptyText={anyPicks ? 'No players on your roster yet.' : 'Your roster is empty.'}
      />
      <PickPicker
        picks={view.myPicks}
        picked={givePicks}
        onToggle={(id) => toggle(givePicks, setGivePicks, id)}
        full={giveCount >= view.maxPerSide}
        mine
      />
    </Stack>
  );
  const getList = (
    <Stack gap={3}>
      <RosterPicker
        rows={view.theirs}
        picked={get}
        onToggle={(id) => toggle(get, setGet, id)}
        full={getCount >= view.maxPerSide}
        emptyText={anyPicks ? 'No players on their roster yet.' : 'Pick a team first.'}
      />
      <PickPicker
        picks={view.theirPicks}
        picked={getPicks}
        onToggle={(id) => toggle(getPicks, setGetPicks, id)}
        full={getCount >= view.maxPerSide}
        mine={false}
      />
    </Stack>
  );
  const partner = view.partner;
  // Who the trade is with, and the way to another team.
  const partnerLine = (team: NonNullable<typeof partner>) => (
    <Line spread gap={2}>
      <Line gap={2}>
        <TeamMark team={team} size={22} />
        <Text weight="strong">{team.name}</Text>
      </Line>
      {!seed.counterOf ? (
        <Button
          variant="quiet"
          size="xs"
          onClick={() => {
            setPartner(null);
            setGet([]);
            setGetPicks([]);
          }}
        >
          Change team
        </Button>
      ) : null}
    </Line>
  );

  return (
    <DetailSheet
      opened
      onClose={onClose}
      title={
        seed.counterOf ? 'Counter offer' : partner ? `Trade with ${partner.ownerName}` : 'New trade'
      }
      wide
    >
      <Stack gap={4}>
        {view.closedReason ? <Callout tone="bad">{view.closedReason}</Callout> : null}

        {!partner ? (
          <Stack gap={2}>
            <Caption>Trade with</Caption>
            <Card>
              {view.partners.map((p) => (
                <ListRow
                  key={p.team.id}
                  lead={<TeamMark team={p.team} size={26} />}
                  onClick={() => setPartner(p.team.id)}
                  trail={<Text tone="secondary">›</Text>}
                >
                  <RowName>{p.team.name}</RowName>
                  <RowSub>
                    {p.team.ownerName} · {p.team.record.wins}–{p.team.record.losses}
                    {p.team.record.ties ? `–${p.team.record.ties}` : ''}
                    {p.openOffers
                      ? ` · ${p.openOffers} open offer${p.openOffers === 1 ? '' : 's'} with you`
                      : ''}
                  </RowSub>
                </ListRow>
              ))}
            </Card>
          </Stack>
        ) : (
          <>
            {phone ? (
              <>
                {/* WHO WITH AND WHICH SIDE STAY AT THE TOP while a roster
                    scrolls under them (B-486): the switch, with each side's
                    count, left with the first few rows. */}
                <PinnedTop>
                  <Stack gap={3}>
                    {partnerLine(partner)}
                    <Segments
                      label="Which side"
                      value={side}
                      onChange={setSide}
                      items={[
                        { id: 'get', label: `You get (${getCount})` },
                        { id: 'give', label: `You give (${giveCount})` },
                      ]}
                    />
                  </Stack>
                </PinnedTop>
                {side === 'get' ? getList : giveList}
              </>
            ) : (
              <>
                {partnerLine(partner)}
                {/* Each side's name stays over its roster while it scrolls
                    (B-486). */}
                <div className="fsp-trade-sides">
                  <Stack gap={2}>
                    <div className="fsp-trade-pick-head">
                      <Caption>You get · up to {view.maxPerSide}</Caption>
                    </div>
                    {getList}
                  </Stack>
                  <Stack gap={2}>
                    <div className="fsp-trade-pick-head">
                      <Caption>You give · up to {view.maxPerSide}</Caption>
                    </div>
                    {giveList}
                  </Stack>
                </div>
              </>
            )}

            {view.mineNeedsDrop ? (
              <Stack gap={2}>
                <Caption>Drop someone: your roster would be over a limit</Caption>
                <Card>
                  {view.myDropOptions
                    .filter((o) => o.droppable)
                    .map((o) => {
                      const on = myDrops.includes(o.player.id);
                      return (
                        <ListRow
                          key={o.player.id}
                          lead={<Tick on={on} />}
                          leadSize="sm"
                          checked={on}
                          selected={on}
                          onClick={() => toggle(myDrops, setMyDrops, o.player.id)}
                          trail={
                            // None in a league that keeps no projections (§15c).
                            o.projection !== null ? (
                              <Text tone="secondary" size="xs" numeric>
                                <Proj>{num(o.projection)}</Proj>
                              </Text>
                            ) : undefined
                          }
                        >
                          <RowName marks={<PlayerMarks player={o.player} />}>
                            {o.player.name}
                          </RowName>
                          <RowSub>
                            {o.slot}
                            {o.locked ? ' · game started: the trade waits for the week to end' : ''}
                          </RowSub>
                        </ListRow>
                      );
                    })}
                </Card>
              </Stack>
            ) : null}
            {view.theirsNeedsDrop ? (
              <Text tone="secondary" size="xs" block>
                {partner.ownerName}'s roster would be over the limit. They pick a drop when they
                accept.
              </Text>
            ) : null}

            {view.readout ? (
              <Card padded>
                <Stack gap={1}>
                  <Caption>The read-out</Caption>
                  {view.readout.sentences.map((s, i) => (
                    <Text key={i} block>
                      {s}
                    </Text>
                  ))}
                </Stack>
              </Card>
            ) : tools.tradeAnalysis ? (
              <Text tone="secondary" block>
                {anyPicks
                  ? 'Pick players or draft picks on either side and the read-out explains the deal.'
                  : 'Pick players on either side and the read-out explains the deal.'}
              </Text>
            ) : null}

            <TextArea
              placeholder="A note (optional): why this works for both of you"
              maxLength={300}
              value={note}
              onChange={setNote}
            />

            {error ? <Callout tone="bad">{error}</Callout> : null}
            {!error && view.problem && giveCount + getCount > 0 ? (
              <Callout>{view.problem}</Callout>
            ) : null}
            <Button
              variant="primary"
              size="md"
              full
              onClick={sendIt}
              disabled={
                busy || fresh === null || view.problem !== null || view.closedReason !== null
              }
            >
              {seed.counterOf ? 'Send counter' : `Send to ${partner.ownerName}`}
            </Button>
          </>
        )}
      </Stack>
    </DetailSheet>
  );
}
